import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { UserService } from '../../services/users'
import { StudentSummaryService } from '../../services/studentSummaries'
import { GroupService } from '../../services/groups'
import type { StudentWithSummary, AggregatedStudentReport } from '../../types/studentSummary'
import StudentListFilters from './StudentListFilters'
import type { RosterFilterValue } from './RosterFilterBar'
import AggregatedReportCard from './AggregatedReportCard'
import StudentListTable from './StudentListTable'
import GroupListPanel from './GroupListPanel'
import { formatNumber } from '../../utils/formatNumber'
import { compareStudentsForRoster } from '../../utils/gradeClass'

interface StudentListTabContentProps {
  schoolId: string
}

const StudentListTabContent = ({ schoolId }: StudentListTabContentProps) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [students, setStudents] = useState<StudentWithSummary[]>([])
  const [filteredStudents, setFilteredStudents] = useState<StudentWithSummary[]>([])
  const [aggregateReport, setAggregateReport] = useState<AggregatedStudentReport | null>(null)
  const [loadingReport, setLoadingReport] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  // Filter state - initialize from URL params
  const [selectedGrade, setSelectedGrade] = useState<string | null>(searchParams.get('grade'))
  const [selectedClass, setSelectedClass] = useState<string | null>(searchParams.get('class'))
  const [selectedGroup, setSelectedGroup] = useState<string | null>(searchParams.get('groupId'))
  const [searchText, setSearchText] = useState(searchParams.get('q') ?? '')
  const [selectedGroupName, setSelectedGroupName] = useState<string | null>(null)

  // Group management state
  const [showGroupManagement, setShowGroupManagement] = useState(false)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 100

  // Calculate average video and conversation counts
  const calculateAverages = useCallback(() => {
    if (students.length === 0) {
      return { avgVideos: 0, avgConversations: 0 }
    }

    const totalVideos = students.reduce((sum, student) => sum + (student.videoCount || 0), 0)
    const totalConversations = students.reduce((sum, student) => sum + (student.chatCount || 0), 0)

    // Round to 1 decimal place
    const avgVideos = Math.round((totalVideos / students.length) * 10) / 10
    const avgConversations = Math.round((totalConversations / students.length) * 10) / 10

    return { avgVideos, avgConversations }
  }, [students])

  const averages = calculateAverages()

  const loadStudentData = useCallback(async () => {
    if (!schoolId) {
      setError('学校情報が見つかりません')
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError('')

      // Load group name if filtering by group
      if (selectedGroup) {
        const groupData = await GroupService.getGroupWithMembers(selectedGroup)
        setSelectedGroupName(groupData?.name || null)
      } else {
        setSelectedGroupName(null)
      }

      // Get students with activity counts
      const studentsWithActivity = await UserService.getStudentsWithActivity(
        schoolId,
        {
          grade: selectedGrade || undefined,
          class: selectedClass || undefined,
          groupId: selectedGroup || undefined
        }
      )

      // Get summaries for all students
      const studentIds = studentsWithActivity.map(s => s.id)
      const summariesMap = await StudentSummaryService.getSummariesForStudents(studentIds)

      // Combine student data with summaries
      const studentsWithSummaries: StudentWithSummary[] = studentsWithActivity.map(student => ({
        id: student.id,
        name: student.name,
        nickname: student.nickname,
        email: student.email,
        grade: student.grade,
        class: student.class,
        attendance_number: student.attendance_number,
        videoCount: student.videoCount || 0,
        chatCount: student.chatCount || 0,
        latestSummary: summariesMap.get(student.id) || null
      }))

      // Sort by grade, class, and attendance number
      studentsWithSummaries.sort(compareStudentsForRoster)

      setStudents(studentsWithSummaries)
    } catch (error) {
      console.error('Failed to load student data:', error)
      setError('生徒データの読み込みに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }, [schoolId, selectedGrade, selectedClass, selectedGroup])

  const loadAggregateReport = useCallback(async () => {
    if (!schoolId) return

    try {
      setLoadingReport(true)

      // Determine aggregation level based on filters
      const level = selectedGroup
        ? 'group'
        : selectedGrade && selectedClass
        ? 'class'
        : selectedGrade
        ? 'grade'
        : 'school'

      const report = await StudentSummaryService.getAggregatedReport(
        schoolId,
        level,
        selectedGrade || undefined,
        selectedClass || undefined,
        selectedGroup || undefined
      )

      setAggregateReport(report)
    } catch (error) {
      console.error('Failed to load aggregate report:', error)
    } finally {
      setLoadingReport(false)
    }
  }, [schoolId, selectedGrade, selectedClass, selectedGroup])

  useEffect(() => {
    loadStudentData()
  }, [loadStudentData])

  useEffect(() => {
    loadAggregateReport()
  }, [loadAggregateReport])

  useEffect(() => {
    // 名前・メールアドレス検索は取得済みの生徒に対してかける（集計レポートは学年・クラス・グループ単位のまま）
    const q = searchText.trim().toLowerCase()
    setFilteredStudents(
      q
        ? students.filter(s =>
            [s.name, s.nickname, s.email].some(v => v?.toLowerCase().includes(q))
          )
        : students
    )
    setCurrentPage(1) // Reset to first page when students change
  }, [students, searchText])

  const handleFilterChange = useCallback((filters: RosterFilterValue) => {
    // Update URL params
    const newParams = new URLSearchParams(searchParams)

    // Preserve tab param
    const tab = newParams.get('tab')

    // Clear all filter params first
    newParams.delete('grade')
    newParams.delete('class')
    newParams.delete('groupId')
    newParams.delete('q')

    setSearchText(filters.search)
    if (filters.search) newParams.set('q', filters.search)

    if (filters.groupId) {
      // When group is selected, clear grade and class
      setSelectedGrade(null)
      setSelectedClass(null)
      setSelectedGroup(filters.groupId)
      newParams.set('groupId', filters.groupId)
    } else {
      // When grade/class is selected, clear group
      setSelectedGrade(filters.grade)
      setSelectedClass(filters.class)
      setSelectedGroup(null)
      if (filters.grade) newParams.set('grade', filters.grade)
      if (filters.class) newParams.set('class', filters.class)
    }

    // Restore tab param if it existed
    if (tab) newParams.set('tab', tab)

    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams])

  const handleCSVExport = () => {
    StudentSummaryService.exportStudentListCSV(filteredStudents, selectedGroupName || undefined)
  }

  const handleManageGroups = () => {
    setShowGroupManagement(true)
  }

  const handleGroupsUpdated = () => {
    // Reload student data when groups are updated
    loadStudentData()
    loadAggregateReport()
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Get students for current page
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedStudents = filteredStudents.slice(startIndex, endIndex)
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage)

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-red-800 mb-2">エラーが発生しました</h3>
        <p className="text-red-700">{error}</p>
        <button
          onClick={loadStudentData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200"
        >
          再読み込み
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">生徒の状況</h2>
        <span className="text-sm text-gray-500">{formatNumber(filteredStudents.length)}名の生徒</span>
      </div>

      {/* Filters */}
      <StudentListFilters
        schoolId={schoolId}
        value={{ grade: selectedGrade, class: selectedClass, groupId: selectedGroup, search: searchText }}
        onFilterChange={handleFilterChange}
        onCSVExport={handleCSVExport}
        onManageGroups={handleManageGroups}
        students={filteredStudents}
      />

      {/* Aggregated Report */}
      {students.length > 0 && (
        <AggregatedReportCard
          report={aggregateReport}
          loading={loadingReport}
          averageVideoCount={averages.avgVideos}
          averageConversationCount={averages.avgConversations}
          groupName={selectedGroupName}
        />
      )}

      {/* Student Table */}
      <StudentListTable
        students={paginatedStudents}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        schoolId={schoolId}
        filterGrade={selectedGrade}
        filterClass={selectedClass}
        filterGroupId={selectedGroup}
      />

      {/* Group Management Panel */}
      <GroupListPanel
        schoolId={schoolId}
        isOpen={showGroupManagement}
        onClose={() => setShowGroupManagement(false)}
        onGroupUpdated={handleGroupsUpdated}
      />
    </div>
  )
}

export default StudentListTabContent
